import { useEffect, useMemo, useState } from 'react'
import { FACESHEET_COLUMNS_SETTINGS } from '../data/facesheetColumnsSettings'
import { getPreference, savePreference } from '../api/preferences'

type Props = {
  open: boolean
  onClose: () => void
}

export function FacesheetSettingsModal({ open, onClose }: Props) {
  const sections = FACESHEET_COLUMNS_SETTINGS.sections
  const toggleDefs = FACESHEET_COLUMNS_SETTINGS.toggles

  const initialChecked = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const sec of sections) {
      for (const r of sec.rows) map[r.value] = r.checked
    }
    return map
  }, [])

  const initialToggles = useMemo(() => {
    const map: Record<string, string> = {}
    for (const t of toggleDefs) {
      const selected = t.options.find((o) => o.checked) || t.options[0]
      map[t.name] = selected?.value || ''
    }
    return map
  }, [])

  const [checked, setChecked] = useState(initialChecked)
  const [toggles, setToggles] = useState(initialToggles)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!open) return
    void getPreference<{ checked: Record<string, boolean>; toggles: Record<string, string> }>('clients.facesheet')
      .then((saved) => {
        if (!saved) return
        setChecked(saved.checked)
        setToggles(saved.toggles)
      })
  }, [open])

  if (!open) return null

  const selectedCount = Object.values(checked).filter(Boolean).length

  const onSave = async () => {
    if (selectedCount === 0) {
      setMsg('Please select at least one profile column.')
      return
    }
    await savePreference('clients.facesheet', { checked, toggles })
    setMsg('Profile Details Settings updated successfully.')
    setTimeout(() => {
      setMsg('')
      onClose()
    }, 700)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="facesheet-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__head">
          <h2 id="facesheet-settings-title">Client Profile Detail Settings</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-panel__body">
          <p className="muted" style={{ margin: '0 0 .85rem', fontSize: '.86rem' }}>
            Profile Details — choose which fields appear on the facesheet.
          </p>

          <div className="fs-print-grid">
            {sections.map((sec) => (
              <section key={sec.title} className="fs-print-section">
                <h3>{sec.title}</h3>
                <table className="fs-print-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Column name</th>
                      <th>Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.rows.map((r) => (
                      <tr key={r.value}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!checked[r.value]}
                            disabled={!!r.locked}
                            onChange={(e) =>
                              setChecked((prev) => ({
                                ...prev,
                                [r.value]: (e.target as HTMLInputElement).checked,
                              }))
                            }
                          />
                        </td>
                        <td>
                          {r.label}
                          {r.locked ? <small className="muted"> (required)</small> : null}
                        </td>
                        <td className="muted">{r.priority || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>

          {toggleDefs.length > 0 ? (
            <div className="fs-print-toggles">
              {toggleDefs.map((t) => (
                <div key={t.name} className="fs-print-toggle">
                  <span>{t.title}</span>
                  <div className="fs-print-toggle__opts">
                    {t.options.map((o) => {
                      const isYes = /YES$/i.test(o.value)
                      return (
                        <label key={o.value}>
                          <input
                            type="radio"
                            name={t.name}
                            checked={toggles[t.name] === o.value}
                            onChange={() =>
                              setToggles((prev) => ({ ...prev, [t.name]: o.value }))
                            }
                          />
                          {isYes ? 'Yes' : 'No'}
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="modal-panel__foot">
          {msg ? (
            <span className="muted">{msg}</span>
          ) : (
            <span className="muted">{selectedCount} columns selected</span>
          )}
          <div style={{ flex: '1' }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void onSave()}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
